import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Button,
  Box,
  CssBaseline,
  Paper,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Divider,
  Tooltip,
  Fade
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LinkIcon from '@mui/icons-material/Link';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import theme from './theme';
import Admin from './pages/Admin';
import BindLocation from './pages/BindLocation';
import QRManage from './pages/QRManage';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Navigation menu with conditional rendering based on auth status
const NavigationMenu = () => {
  const { isAuthenticated, username, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = () => {
    handleMenuClose();
    logout();
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const navItems = [
    { path: '/', label: 'Home', icon: <HomeIcon /> },
    ...(isAuthenticated ? [
      { path: '/admin', label: 'Admin', icon: <AdminPanelSettingsIcon /> },
      { path: '/bind-location', label: 'Bind Location', icon: <LinkIcon /> },
      { path: '/qr-manage', label: 'Manage QR Codes', icon: <QrCodeIcon /> }
    ] : [])
  ];
  
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Beelabs QR System
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem 
            key={item.path} 
            component={Link} 
            to={item.path} 
            button 
            selected={isActive(item.path)}
            sx={{
              color: 'inherit',
              bgcolor: isActive(item.path) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(25, 118, 210, 0.12)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        {!isAuthenticated && (
          <ListItem 
            component={Link} 
            to="/login" 
            button 
            selected={isActive('/login')}
            sx={{
              color: 'inherit',
              bgcolor: isActive('/login') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(25, 118, 210, 0.12)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Login" />
          </ListItem>
        )}
        {isAuthenticated && (
          <ListItem button onClick={logout} sx={{ color: 'inherit' }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        )}
      </List>
    </Box>
  );
  
  return (
    <>
      <AppBar position="sticky" elevation={2}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography 
            variant="h6" 
            component={Link} 
            to="/" 
            sx={{ 
              flexGrow: 1, 
              textDecoration: 'none', 
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <QrCodeIcon fontSize="large" />
            Beelabs QR System
          </Typography>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button 
                  key={item.path}
                  component={Link} 
                  to={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  sx={{ 
                    mx: 0.5, 
                    px: 2,
                    position: 'relative',
                    '&::after': isActive(item.path) ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 8,
                      right: 8,
                      height: 3,
                      bgcolor: 'white',
                      borderTopLeftRadius: 3,
                      borderTopRightRadius: 3,
                    } : {}
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
          
          {isAuthenticated ? (
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
              <Tooltip title={`Logged in as ${username}`} arrow TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                <IconButton 
                  onClick={handleMenuOpen}
                  size="small"
                  sx={{ ml: 2, border: 1, borderColor: 'rgba(255,255,255,0.5)' }}
                  aria-controls="user-menu"
                  aria-haspopup="true"
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                    {username.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem sx={{ minWidth: 150 }}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {username}
                  </ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Logout
                  </ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            !isMobile && (
              <Button 
                color="inherit" 
                component={Link} 
                to="/login"
                startIcon={<PersonIcon />}
                variant={isActive('/login') ? "outlined" : "text"}
                sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
              >
                Login
              </Button>
            )
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <BrowserRouter>
          <CssBaseline />
          <div className="App">
            <NavigationMenu />
            
            <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3, 
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  minHeight: '70vh'
                }}
              >
                <Routes>
                  <Route path="/" element={
                    <Box sx={{ textAlign: 'center', my: { xs: 4, md: 8 } }}>
                      <QrCodeIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                        Welcome to Beelabs QR System
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', mb: 4 }}>
                        Use the navigation above to manage QR codes and locations.
                      </Typography>
                      
                      {/* Quick action buttons on homepage */}
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', mt: 4 }}>
                        <Button 
                          component={Link}
                          to="/admin"
                          variant="contained" 
                          size="large"
                          startIcon={<AdminPanelSettingsIcon />}
                          sx={{ px: 4, py: 1.5 }}
                        >
                          Go to Admin Dashboard
                        </Button>
                        <Button 
                          component={Link}
                          to="/qr-manage"
                          variant="outlined" 
                          size="large"
                          startIcon={<QrCodeIcon />}
                          sx={{ px: 4, py: 1.5 }}
                        >
                          Manage QR Codes
                        </Button>
                      </Box>
                    </Box>
                  } />
                  <Route path="/login" element={<Login />} />
                  
                  {/* Protected routes */}
                  <Route path="/admin" element={
                    <PrivateRoute>
                      <Admin />
                    </PrivateRoute>
                  } />
                  <Route path="/bind-location" element={
                    <PrivateRoute>
                      <BindLocation />
                    </PrivateRoute>
                  } />
                  <Route path="/qr-manage" element={
                    <PrivateRoute>
                      <QRManage />
                    </PrivateRoute>
                  } />
                </Routes>
              </Paper>
            </Container>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
